import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { DocumentService } from 'src/app/services/document.service';
import { Document } from 'src/app/shared/models/documents/document';
import { UserIdentifiers } from 'src/app/shared/models/users/user-identifiers';
import { UpdateDocumentRequest } from 'src/app/shared/models/documents/update-document-request';
import { SelectOption } from 'src/app/shared/models/general/select-option';
import { DocumentRequest } from 'src/app/shared/models/documents/document-request';

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss'],
})
export class DocumentComponent implements OnChanges, OnDestroy {
  @Input() userIdentifiers: UserIdentifiers;
  @Input() fullName: string;
  @Input() spouseFullName: string;
  selectedDocType: number = DocType.Onboarding;
  docs: Document[] = [];
  errorResponse: HttpErrorResponse;
  showError: boolean = false;
  showLoanAgreement: boolean = false;
  loadingGrid: boolean = false;
  sendingDoc: boolean = false;
  downloadingDoc: boolean = false;
  busyText: string = 'Busy...';
  selectedDocTypeName: string;
  private subs: Subscription[] = [];

  isInProgress: boolean = false;

  docTypeOptions: SelectOption[] = [
    { label: 'Onboarding', value: DocType.Onboarding },
    { label: 'Broad Loan Agreement', value: DocType.BroadLoanAgreement },
    { label: 'Loan Agreement', value: DocType.LoanAgreement },
  ];

  constructor(
    private documentService: DocumentService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userIdentifiers']) {
      //can only do loan agreements with a spouse, so remove the option if there is no spouse
      if (!this.spouseFullName) {
        const valuesToRemove = [DocType.LoanAgreement, DocType.BroadLoanAgreement];
        this.docTypeOptions = this.docTypeOptions.filter((option) => !valuesToRemove.includes(option.value));
      }
      this.fillDocumentGrid();
    }
  }

  onSelectionChange(): void {
    this.checkForInProgressStatus();
  }

  private fillDocumentGrid() {
    const userId = this.userIdentifiers?.userId;
    if (userId == null) {
      this.loadingGrid = false;
      return;
    }

    this.loadingGrid = true;
    const sub = this.documentService.GetDocumentsByUserId(userId).subscribe({
      next: (documents: Document[]) => {
        this.docs = documents;
        this.checkForInProgressStatus();
        this.loadingGrid = false;
      },
      error: (err) => {
        this.loadingGrid = false;
        this.parseAndDisplayError(err);
      },
    });
    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private createOnboardingDocRequest(): DocumentRequest {
    let onboardingRequest = new DocumentRequest();
    onboardingRequest.$type = 'Trakly.Models.API.Document.OnboardingDocRequest, Trakly.Models';
    return onboardingRequest;
  }

  private createBroadLoanAgreementDocRequest(): DocumentRequest {
    let broadLoanAgreementRequest = new DocumentRequest();
    broadLoanAgreementRequest.$type = 'Trakly.Models.API.Document.BroadLoanAgreementDocRequest, Trakly.Models';
    return broadLoanAgreementRequest;
  }

  sendDoc() {
    this.sendingDoc = true;
    let docRequest = new DocumentRequest();

    switch (this.selectedDocType) {
      case DocType.Onboarding:
        docRequest = this.createOnboardingDocRequest();
        break;
      case DocType.BroadLoanAgreement:
        docRequest = this.createBroadLoanAgreementDocRequest();
        break;
      default:
        alert(`Cannot send doc with unknown doc type - ${this.selectedDocType}`);
    }

    docRequest.userIdentifiers = this.userIdentifiers;

    const sub = this.documentService.SendDocumentData(docRequest).subscribe({
      next: (newDoc: Document) => {
        this.handleRecordUpdated(newDoc);
        this.sendingDoc = false;
      },
      error: (err) => {
        this.sendingDoc = false;
        this.parseAndDisplayError(err);
      },
    });
    this.subs.push(sub);
  }

  async downloadDoc(doc: Document): Promise<void> {
    if (!this.isValidUrl(doc.downloadLink)) {
      console.warn('Attempted to download with an invalid URL structure.');
    }

    doc.busy = true;
    this.busyText = 'Downloading...';
    this.cd.detectChanges();

    let objectUrl: string | null = null;

    try {
      const fetchUrl = doc.downloadLink;

      const maxRetries = 3;
      let response: Response | undefined;
      let delay = 1000;

      for (let i = 0; i < maxRetries; i++) {
        try {
          response = await fetch(fetchUrl, {
            method: 'GET',
            credentials: 'omit',
          });

          if (response.ok) break;

          // If we get a 403 or 400, retrying won't help because the URL is wrong
          if (response.status === 403 || response.status === 400) break;
        } catch (e) {
          if (i < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            delay *= 2;
          } else {
            throw e;
          }
        }
      }

      if (!response || !response.ok) {
        const errorText = await response.text();
        console.error('Server Error Details:', errorText);
        throw new Error(`Download failed (${response?.status}). Check console for details.`);
      }

      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${this.fullName}_${doc.type}_${doc.id}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error(error);
      alert(`Download failed: ${error}`);
    } finally {
      doc.busy = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      this.cd.detectChanges();
    }
  }

  cancelDoc(doc: Document) {
    doc.busy = true;
    this.busyText = 'Cancelling...';
    let updateDocumentRequest = new UpdateDocumentRequest();
    updateDocumentRequest.idNumber = this.userIdentifiers.idNumber;
    updateDocumentRequest.status = DocumentStatus.Cancelled;
    updateDocumentRequest.documentType = DocType[doc.type];

    const sub = this.documentService.UpdateDocumentData(updateDocumentRequest).subscribe({
      next: (updatedDoc: Document) => {
        this.handleRecordUpdated(updatedDoc);
        doc.busy = false;
      },
      error: (err) => {
        doc.busy = false;
        this.parseAndDisplayError(err);
      },
    });
    this.subs.push(sub);
  }

  parseAndDisplayError(error: any) {
    this.errorResponse = error;
    this.showError = true;
  }

  displayNotificationDocSaved(doc: Document) {
    doc.recentlyUpdated = true;
    setTimeout(() => {
      doc.recentlyUpdated = false;
    }, 3000);
  }

  private checkForInProgressStatus() {
    this.selectedDocTypeName = DocType[this.selectedDocType];
    const foundIndex = this.docs.findIndex((x) => x.status === 'InProgress' && x.type === this.selectedDocTypeName);
    this.isInProgress = foundIndex > -1;
  }

  private findExistingDocInList(savedDocId: number): Document {
    //find this specific doc in the array used to fill the grid and then update it
    const foundIndex = this.docs.findIndex((x) => x.id === savedDocId);
    let original = this.docs[foundIndex];

    return original;
  }

  handleRecordUpdated(savedDoc: Document) {
    const original = this.findExistingDocInList(savedDoc.id);

    if (original) {
      original.status = savedDoc.status;
      original.message = savedDoc.message;
      original.dateSent = savedDoc.dateSent;
      this.displayNotificationDocSaved(original);
    } else {
      this.docs.unshift(savedDoc);
      this.displayNotificationDocSaved(savedDoc);
    }

    this.checkForInProgressStatus();
  }

  showLoanAgreementForm() {
    this.showLoanAgreement = true;
  }

  handleModalClosed() {
    this.showLoanAgreement = false;
  }

  handleError(err: any) {
    this.parseAndDisplayError(err);
  }
  canSendDocDirect(): boolean {
    const allowedTypes = [DocType.Onboarding, DocType.BroadLoanAgreement];

    return !this.sendingDoc && allowedTypes.includes(this.selectedDocType);
  }

  canSendLoanAgreementDoc(): boolean {
    return !this.sendingDoc && this.selectedDocType === DocType.LoanAgreement;
  }

  canDownloadDoc(doc: Document): boolean {
    if (!doc.downloadLink) {
      return false;
    }

    return doc.status === 'Signed';
  }

  isValidUrl(url: string): boolean {
    if (!url || url.trim().length === 0) return false;

    let decodedUrl = url.trim();
    try {
      decodedUrl = decodeURIComponent(url.trim());
    } catch (e) {
      // If decoding fails (e.g., malformed encoding), treat it as-is but don't crash
      // The URL constructor later handles if it's truly invalid for fetch.
    }

    const lowerCaseDecodedUrl = decodedUrl.toLowerCase();

    // Check if it starts with http:// or https:// after decoding
    return lowerCaseDecodedUrl.startsWith('http://') || lowerCaseDecodedUrl.startsWith('https://');
  }

  getStatusBadge(status: string): { [key: string]: boolean } {
    return {
      'badge-danger': status === 'Failed',
      'badge-info': status === 'InProgress',
      'badge-success': status === 'Signed',
      'badge-light': status === 'Cancelled',
    };
  }
}

enum DocType {
  None = 0,
  LoanAgreement = 1,
  Onboarding = 2,
  BroadLoanAgreement = 4,
  WealthOnboarding = 5,
}

enum DocumentStatus {
  None = 0,
  InProgress = 1,
  Signed = 2,
  Cancelled = 98,
  Failed = 99,
}
